import React from 'react';
import styled, { useTheme } from 'styled-components';
import Link from 'next/link';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useRouter } from 'next/router';
import { useKioskPro } from 'src/hooks/use-kiosk-pro';
import { KIOSK_PRO_MEDIA } from 'src/components/kiosk-pro/kiosk-pro-shared/kiosk-pro-shared.styles';

import useTranslation from 'hooks/use-translation';
import useProductQueryParams from 'hooks/use-product-query-params';
import useUI from 'hooks/use-ui';
import { useHideSalesLanguage } from 'src/hooks/use-hide-sales-language';

import SubcategoriesPills from 'components/subcategories-pills';
import ChevronIcon from 'shared/components/chevron-icon';
import { MobileOnly, DesktopOnly, Clickable } from 'shared/components';
import IndividualBogoCard from 'shared/components/specials/individual-bogo-card';
import { Typography } from 'src/typography';
import { MenuSortV2 } from 'src/components/menu-sort-v2';
import SvgChevronIcon from 'src/assets/chevron-icon';

export default function ContentHeader(props) {
  const {
    children,
    heading,
    className,
    searchString,
    count,
    dispensary,
    isMobileEcommApp,
    selectedSpecial,
    offerSpecialPage,
    saleSpecialPage,
    showExpiredSpecial = false,
    customHeaderComponent,
    belowHeaderComponent,
    hideSort = false,
  } = props;

  const flags = useFlags();
  const router = useRouter();
  const isProductCardsV2Enabled = flags['growth.ecomm.product-card-v2.rollout'] ?? false;
  const hideBorder = isProductCardsV2Enabled || showExpiredSpecial;

  const showSearchHeader = !!searchString;
  const specialPageVariant = offerSpecialPage || saleSpecialPage;

  const isBrands = router.pathname?.includes('brands');
  const isKioskProEnabled = useKioskPro();
  const { isKiosk } = useUI();
  const isKioskProBrandsPage = isKioskProEnabled && isKiosk && isBrands;

  let menuHeaderTitle = (
    <Text>
      {isKioskProBrandsPage ? (
        <KioskProHeading size='medium' tag='h1' casing='capitalize' data-testid='menu-header-title'>
          <StyledBackButton onClick={() => router.back()}>
            <StyledSvgChevronIcon />
          </StyledBackButton>
          {heading}
        </KioskProHeading>
      ) : (
        <ClickableBackHeader onClick={() => router.back()}>
          <BackIconTitle />
          <Typography.Heading size='medium' tag='h1' casing='capitalize' data-testid='menu-header-title'>
            {heading}
          </Typography.Heading>
        </ClickableBackHeader>
      )}
    </Text>
  );

  if (showSearchHeader) {
    menuHeaderTitle = <SearchResultsHeader searchString={searchString} count={count} />;
  }

  const shouldShowSpecialCard = specialPageVariant && selectedSpecial;

  return (
    <OuterContainer hideBorder={hideBorder}>
      <HeadingContainer className={className} data-testid='heading-container'>
        {!isMobileEcommApp && specialPageVariant && <AllSpecialsBreadcrumb dispensary={dispensary} />}

        {(shouldShowSpecialCard || belowHeaderComponent) && (
          <FullWidthContainer>
            {shouldShowSpecialCard && (
              <OfferCardContainer>
                <IndividualBogoCard
                  displayImage={selectedSpecial.menuDisplayImage}
                  displayName={selectedSpecial.menuDisplayName || selectedSpecial.name}
                  displayDescription={selectedSpecial.menuDisplayDescription || selectedSpecial.description}
                  showExpiredSpecial={showExpiredSpecial}
                />
              </OfferCardContainer>
            )}

            {belowHeaderComponent}
          </FullWidthContainer>
        )}

        <MobileOnly>
          {specialPageVariant && (
            <PillContainer>
              <SubcategoriesPills
                isSpecials
                specialId={selectedSpecial?._id}
                specialType={selectedSpecial?.specialType}
              />
            </PillContainer>
          )}
        </MobileOnly>
        {!showExpiredSpecial && (
          <MenuHeaderWithFilters>
            {customHeaderComponent}

            <DesktopOnly>{!isMobileEcommApp && menuHeaderTitle}</DesktopOnly>
            {!isMobileEcommApp && isKioskProBrandsPage && <MobileOnly>{menuHeaderTitle}</MobileOnly>}

            {!hideSort && !isKioskProBrandsPage && (
              <MobileOnly>
                <MenuSortV2 />
              </MobileOnly>
            )}

            {children && <Filters>{children}</Filters>}
          </MenuHeaderWithFilters>
        )}
      </HeadingContainer>
    </OuterContainer>
  );
}

const MenuHeaderWithFilters = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  width: 100%;
`;

const AllSpecialsBreadcrumb = ({ dispensary }) => {
  const { customized } = useTheme();
  const UI = useUI();
  const { t } = useTranslation();
  const hideSalesLanguage = useHideSalesLanguage(dispensary?.location?.state);
  const pageTitle = hideSalesLanguage ? t(`common.all_offers`, `All Offers`) : t(`common.all_specials`, `All Specials`);

  return (
    <BreadcrumbContainer>
      <Link href={`/${UI.dispensaryRootUrl}/${dispensary.cName}/specials`}>
        <StyledSpan>
          <BackIcon fill={customized.colors.buttonsLinks} />
          {pageTitle}
        </StyledSpan>
      </Link>
    </BreadcrumbContainer>
  );
};

const SearchResultsHeader = ({ count, searchString }) => {
  const { t } = useTranslation();
  const { resetQueryParams } = useProductQueryParams();

  return (
    <SearchResultsContainer data-testid='menu-header-title'>
      {t('common.showing', {
        defaultValue: 'Showing {{count}} result for:', // showing 1 result for
        defaultValue_0: 'Showing {{count}} results for:', // showing 0 results for
        defaultValue_plural: 'Showing {{count}} results for:', // showing 2+ results for
        count,
      })}{' '}
      <span id='search-string'>"{searchString}"</span>
      <Clickable id='clear' onClick={resetQueryParams}>
        {t('common.clear', 'clear')}
      </Clickable>
    </SearchResultsContainer>
  );
};

const BreadcrumbContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledSpan = styled.span`
  font-weight: bold;
  font-size: 14px;
  line-height: 175%;
  color: ${({ theme }) => theme.customized.colors.buttonsLinks};
  margin-bottom: 10px;

  &:hover {
    cursor: pointer;
  }
`;

const BackIcon = styled(ChevronIcon)`
  transform: rotate(90deg);
  margin-right: 5px;
  display: inline-block;
`;

const SearchResultsContainer = styled.div`
  text-transform: initial;
  font-size: 14px;

  #search-string {
    font-weight: bold;
  }
  #clear {
    color: ${({ theme }) => theme.customized.colors.buttonsLinks};
    display: inline;
    text-transform: lowercase;
    margin-left: 5px;
  }
`;

const HeadingContainer = styled.header`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const OuterContainer = styled.div`
  ${(props) =>
    !props.hideBorder &&
    `
  border-bottom: 1px solid ${props.theme.colors.blueGrey[90]};
`}

  padding-bottom: 16px;
  ${({ theme }) => theme.breakpoints.down('md')} {
    padding-bottom: 15px;
  }
`;

const Text = styled.div`
  flex: 1 0 0%;
  min-width: 0;
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.primaryBlack};
`;

const Filters = styled.div`
  flex: 0 0 auto;
  min-width: 0;
  padding-left: 12px;
  display: flex;
  align-items: center;
`;

const PillContainer = styled.div`
  ${({ theme }) => theme.breakpoints.down(`md`)} {
    width: 100vw;
    .subcategories-pills {
      margin: 11px 0 40px;
      div {
        &:nth-child(1) {
          padding-left: 0px;
        }
      }
    }
  }
`;

const FullWidthContainer = styled.div`
  width: 100%;
`;

const OfferCardContainer = styled.div`
  width: 100%;
  ${({ theme }) => theme.breakpoints.down(`md`)} {
    margin-bottom: 24px;
    margin-top: 6px;
  }
`;

const StyledBackButton = styled.button`
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 800px;

  background: ${({ theme }) => theme.colors.grey[95]};
  border: none;
  cursor: pointer;
  box-shadow: 0px 4px 6px 0px rgba(0, 0, 0, 0.2);
`;

const StyledSvgChevronIcon = styled(SvgChevronIcon)`
  transform: rotate(90deg);
  color: ${({ theme }) => theme.colors.black};
  width: 16px;
`;

const KioskProHeading = styled(Typography.Heading)`
  font-size: 36px;
  display: flex;
  align-items: center;
  gap: 20px;

  @media ${KIOSK_PRO_MEDIA.landscapeMaxWidth} {
    font-size: 28px;
  }

  @media ${KIOSK_PRO_MEDIA.portraitMaxWidth} {
    font-size: 28px;
  }
`;

const BackIconTitle = styled(BackIcon)`
  margin-right: 10px;
`;

const ClickableBackHeader = styled.button`
  display: flex;
  align-items: center;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
`;
